import React from 'react';
import { luxe, gradientCss } from '@/lib/luxe';
import { Loader2 } from 'lucide-react';

export function GlassCard({ children, className = '', style = {}, ...rest }) {
  return (
    <div className={`glass rounded-2xl ${className}`} style={{ borderRadius: 18, ...style }} {...rest}>
      {children}
    </div>
  );
}

export function LuxeButton({ children, variant = 'gold', color, onClick, disabled, type = 'button', className = '', full, ...rest }) {
  let style = { borderRadius: 999, fontWeight: 700, letterSpacing: 0.2, padding: '12px 22px' };
  if (variant === 'gold') {
    style.background = gradientCss(['#F5E6B8', '#E9C46A', '#D4AF37']);
    style.color = '#2A2005';
    style.boxShadow = '0 10px 26px rgba(233,196,106,0.35), inset 0 1px 0 rgba(255,255,255,0.4)';
  } else if (variant === 'blue') {
    style.background = gradientCss(['#4E9EFF', '#6366F1', '#7C3AED']);
    style.color = '#fff';
    style.boxShadow = '0 10px 26px rgba(99,102,241,0.4), inset 0 1px 0 rgba(255,255,255,0.25)';
  } else if (variant === 'pillar') {
    style.background = gradientCss(color?.grad || ['#7C3AED', '#A855F7']);
    style.color = color?.key === 'marketing' ? '#2A2005' : '#fff';
    style.boxShadow = `0 10px 26px ${color?.glow || 'rgba(124,58,237,0.4)'}, inset 0 1px 0 rgba(255,255,255,0.22)`;
  } else if (variant === 'ghost') {
    style.background = 'rgba(255,255,255,0.05)';
    style.color = luxe.text;
    style.border = '1px solid rgba(255,255,255,0.14)';
  } else if (variant === 'danger') {
    style.background = 'rgba(239,68,68,0.14)';
    style.color = '#FCA5A5';
    style.border = '1px solid rgba(239,68,68,0.4)';
  }
  return (
    <button type={type} onClick={onClick} disabled={disabled} className={`luxe-btn ${full ? 'w-full' : ''} ${className}`} style={style} {...rest}>
      {children}
    </button>
  );
}

export function Field({ label, hint, children }) {
  return (
    <label className="block mb-4">
      {label && <span className="block mb-2 text-[11px] font-extrabold uppercase tracking-widest" style={{ color: luxe.muted }}>{label}</span>}
      {children}
      {hint && <span className="block mt-1 text-xs" style={{ color: luxe.muted }}>{hint}</span>}
    </label>
  );
}

const fieldStyle = { color: luxe.text, fontSize: 14, padding: '12px 14px', borderRadius: 12, border: `1px solid ${luxe.border}`, background: luxe.bgElev };

export function Input(props) {
  return (
    <input
      {...props}
      className={`w-full outline-none ${props.className || ''}`}
      style={{ ...fieldStyle, ...props.style }}
      onFocus={(e) => (e.target.style.borderColor = luxe.pink)}
      onBlur={(e) => (e.target.style.borderColor = luxe.border)}
    />
  );
}

export function Textarea(props) {
  return (
    <textarea
      {...props}
      className={`w-full outline-none ${props.className || ''}`}
      style={{ ...fieldStyle, resize: 'vertical', ...props.style }}
      onFocus={(e) => (e.target.style.borderColor = luxe.pink)}
      onBlur={(e) => (e.target.style.borderColor = luxe.border)}
    />
  );
}

export function Select(props) {
  return (
    <select {...props} className={`w-full outline-none ${props.className || ''}`} style={{ ...fieldStyle, ...props.style }}>
      {props.children}
    </select>
  );
}

export function SectionTitle({ eyebrow, title, desc, color }) {
  return (
    <div className="mb-6">
      {eyebrow && <div className="text-[11px] font-extrabold uppercase tracking-[0.2em] mb-2" style={{ color: color || luxe.gold }}>{eyebrow}</div>}
      <h1 className="font-display text-3xl sm:text-4xl font-black" style={{ color: luxe.text, letterSpacing: -0.5 }}>{title}</h1>
      {desc && <p className="mt-2 text-sm max-w-2xl" style={{ color: luxe.textMid }}>{desc}</p>}
    </div>
  );
}

export function StatCard({ label, value, sub, accent }) {
  return (
    <GlassCard className="p-5 hover-lift">
      <div className="text-[10px] font-extrabold uppercase tracking-widest" style={{ color: luxe.muted }}>{label}</div>
      <div className="mt-2 text-2xl font-black" style={{ color: accent || luxe.text }}>{value}</div>
      {sub && <div className="mt-1 text-xs" style={{ color: luxe.muted }}>{sub}</div>}
    </GlassCard>
  );
}

export function LivePill({ label = 'LIVE', color }) {
  const c = color || luxe.success;
  return (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[9px] font-black uppercase tracking-widest" style={{ background: `${c}22`, border: `1px solid ${c}`, color: c }}>
      <span className="w-1.5 h-1.5 rounded-full pulse-glow" style={{ background: c }} /> {label}
    </span>
  );
}

export function Spinner({ size = 18, color }) {
  return <Loader2 size={size} className="animate-spin" style={{ color: color || luxe.pink }} />;
}

export function Empty({ icon: Icon, title, desc }) {
  return (
    <div className="text-center py-16 float-in">
      {Icon && (
        <div className="mx-auto mb-4 w-14 h-14 rounded-2xl flex items-center justify-center" style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}>
          <Icon size={26} style={{ color: luxe.muted }} />
        </div>
      )}
      <div className="font-bold" style={{ color: luxe.textMid }}>{title}</div>
      {desc && <div className="mt-1 text-sm" style={{ color: luxe.muted }}>{desc}</div>}
    </div>
  );
}
